import { useEffect, useState } from 'react';
import { Autocomplete } from '@material-ui/lab';
import { TextField, CircularProgress } from '@material-ui/core';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import {
  createScaffolderFieldExtension,
  FieldExtensionComponentProps,
} from '@backstage/plugin-scaffolder-react';

// Options are the live GitLab groups and subgroups the configured
// integration token can see, so the choice can't be a typo'd path.
export const NamespacePicker = ({
  onChange,
  rawErrors,
  formData,
}: FieldExtensionComponentProps<string>) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(false);
  const [accessError, setAccessError] = useState<string | undefined>();

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
      const response = await fetchApi.fetch(`${baseUrl}/gitlab/namespaces`);
      if (cancelled) return;

      if (response.ok) {
        const { namespaces } = await response.json();
        setOptions(namespaces);
      }
      setLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  // Checks up front whether the account creating the repo can actually
  // create a project here, instead of only finding out once the scaffolder
  // task itself runs the "Validate Namespace" step.
  useEffect(() => {
    let cancelled = false;

    if (!formData) {
      setAccessError(undefined);
      return undefined;
    }

    const checkAccess = async () => {
      setCheckingAccess(true);
      try {
        const baseUrl = await discoveryApi.getBaseUrl('scaffolder');
        const response = await fetchApi.fetch(
          `${baseUrl}/gitlab/namespace-access?namespace=${encodeURIComponent(
            formData,
          )}`,
        );
        if (cancelled) return;

        if (response.ok) {
          const { allowed, reason } = await response.json();
          setAccessError(allowed ? undefined : reason);
        } else {
          setAccessError(undefined);
        }
      } catch (error) {
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.error('Failed to check namespace access', error);
          setAccessError(undefined);
        }
      } finally {
        if (!cancelled) setCheckingAccess(false);
      }
    };

    checkAccess();
    return () => {
      cancelled = true;
    };
  }, [formData, discoveryApi, fetchApi]);

  return (
    <Autocomplete
      loading={loading}
      options={options}
      value={formData ?? null}
      onChange={(_, value) => onChange(value ?? '')}
      renderInput={params => (
        <TextField
          {...params}
          label="GitLab namespace"
          helperText={
            accessError ??
            'The GitLab group/subgroup path the project is created under.'
          }
          error={Boolean(accessError) || rawErrors?.length > 0}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || checkingAccess ? (
                  <CircularProgress size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export const NamespacePickerFieldExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'NamespacePicker',
    component: NamespacePicker,
  }),
);
